import React, { useState } from 'react'; // Import useState
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faLinkedinIn, faWhatsapp } from '@fortawesome/free-brands-svg-icons';


import { faEnvelope, faPhone, faQuestionCircle, faLink, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons'; // Added faCheckCircle, faTimesCircle

export default function Contact() {
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null); // To display success or error
  const [isError, setIsError] = useState(false); // To style success/error message

  // Animation variants (kept the same)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    setIsSubmitting(true);
    setSubmitMessage(null); // Clear previous messages
    setIsError(false);

    try {
      const response = await fetch('https://lumora-1-a0lf.onrender.com/api/contact', { // <--- IMPORTANT: Update with your backend URL
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, message }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage(data.message);
        setIsError(false);
        // Clear form fields on success
        setName('');
        setEmail('');
        setMessage('');
      } else {
        setSubmitMessage(data.message || 'An unexpected error occurred.');
        setIsError(true);
      }
    } catch (error) {
      console.error('Frontend error submitting form:', error);
      setSubmitMessage('Failed to connect to the server. Please try again later.');
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-12 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-4xl w-full space-y-10 p-8 sm:p-10 bg-[#1A1A2E] rounded-3xl shadow-2xl border border-[#3A3A5A]"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <h2 className="mb-4 text-5xl font-extrabold leading-tight text-center text-transparent md:text-6xl bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">
            Connect with Lumora
          </h2>
          <p className="max-w-2xl mx-auto text-xl text-center text-gray-300">
            We're here to guide your cosmic learning journey. Reach out with your questions, feedback, or just to say hello!
          </p>
        </motion.div>

        {/* Separator */}
        <motion.div variants={itemVariants} className="h-px my-8 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></motion.div>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
          {/* Section 1: Send Us a Message (Form) */}
          <motion.div variants={itemVariants}>
            <h3 className="mb-6 text-3xl font-bold text-left text-teal-300">Send Us a Message</h3>
            <form className="space-y-6" onSubmit={handleSubmit}> {/* Added onSubmit handler */}
              <input
                className="w-full p-4 rounded-xl bg-[#2A2A4A] border border-[#3A3A5A] placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-cyan-400 transition duration-300 shadow-inner text-white text-lg"
                type="text"
                placeholder=" Name "
                value={name} // Controlled component
                onChange={(e) => setName(e.target.value)} // Update state on change
                required // HTML5 validation
              />
              <input
                className="w-full p-4 rounded-xl bg-[#2A2A4A] border border-[#3A3A5A] placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-cyan-400 transition duration-300 shadow-inner text-white text-lg"
                type="email"
                placeholder=" Email "
                value={email} // Controlled component
                onChange={(e) => setEmail(e.target.value)} // Update state on change
                required // HTML5 validation
              />
              <textarea
                className="w-full p-4 rounded-xl bg-[#2A2A4A] border border-[#3A3A5A] placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-cyan-400 transition duration-300 shadow-inner resize-none h-40 text-white text-lg"
                placeholder="Your Message "
                value={message} // Controlled component
                onChange={(e) => setMessage(e.target.value)} // Update state on change
                required // HTML5 validation
              ></textarea>
              <button
                className={`w-full px-8 py-4 rounded-xl font-bold text-xl tracking-wider shadow-lg transform hover:scale-[1.01] transition duration-300
                           ${isSubmitting
                              ? 'bg-gray-500 cursor-not-allowed' // Dim when submitting
                              : 'bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 shadow-purple-500/50 hover:shadow-cyan-400/50'
                           }`}
                type="submit" // Ensure button type is submit
                disabled={isSubmitting} // Disable during submission
              >
                {isSubmitting ? 'SENDING...' : 'SEND'}
              </button>

              {/* Submission Feedback Message */}
              {submitMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center p-3 rounded-lg text-lg ${
                    isError ? 'bg-red-600/20 text-red-400' : 'bg-green-600/20 text-green-400'
                  }`}
                >
                  <FontAwesomeIcon icon={isError ? faTimesCircle : faCheckCircle} className="mr-3 text-2xl" />
                  {submitMessage}
                </motion.div>
              )}
            </form>
          </motion.div>

          {/* Section 2: Other Ways to Connect (kept the same as last update) */}
          <motion.div variants={itemVariants}>
            <h3 className="mb-6 text-3xl font-bold text-left text-teal-300">Other Ways to Connect</h3>
            <div className="space-y-6 text-lg text-left text-gray-200">
              <p>
                Got a quick question or prefer a different channel?
                Find us through these cosmic communication methods:
              </p>
              <div>
                <strong className="block mb-1 text-cyan-400">Email Support:</strong>
                <a href="mailto:teamiotrics@gmail.com" className="flex items-center transition duration-300 hover:text-cyan-200">
                  <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-xl" /> teamiotrics@gmail.com
                </a>
              </div>
              <div>
                <strong className="block mb-1 text-cyan-400">Phone (Mon-Sat, 9 AM - 6 PM EST):</strong>
                <div className="flex items-center mt-2">
                  <FontAwesomeIcon icon={faPhone} className="mr-2 text-xl" /> +91 9446188524
                </div>
              </div>
              <div>
                <strong className="block mb-1 text-cyan-400">Social Orbit:</strong>
                <div className="flex mt-2 space-x-4">
                  <a href="https://instagram.com/iotrics_" target="_blank" rel="noopener noreferrer" className="text-2xl text-pink-500 transition duration-300 hover:text-pink-400">
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                  <a href="https://linkedin.com/company/iotricsolutions" target="_blank" rel="noopener noreferrer" className="text-2xl text-blue-700 transition duration-300 hover:text-blue-600">
                    <FontAwesomeIcon icon={faLinkedinIn} />
                  </a>
                  <a href="https://wa.me/919446188524" target="_blank" rel="noopener noreferrer" className="text-2xl text-green-500 transition duration-300 hover:text-green-400">
                    <FontAwesomeIcon icon={faWhatsapp} />
                  </a>
                </div>
              </div>
              <div>
                <strong className="block mb-1 text-cyan-400">Help Center / FAQs:</strong>
                Visit our Knowledge Base
                <a
                  href="https://www.iotrics.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center transition duration-300 hover:text-cyan-200"
                >
                  <FontAwesomeIcon icon={faLink} className="mr-2 text-xl" /> www.iotrics.com
                </a>
                <Link to="/faq" className="flex items-center mt-2 transition duration-300 hover:text-cyan-200">
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Separator */}
        <motion.div variants={itemVariants} className="h-px my-8 bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></motion.div>

        <motion.p variants={itemVariants} className="text-center text-gray-400 text-md sm:text-lg">
          Awaiting your coordinates. Estimated response time: Within 1-2 Earth rotations. 
        </motion.p>
      </motion.div>
    </div>
  );
}